
import React, { useContext, useEffect, useState } from 'react'
import { RouteComponentProps, useLocation } from 'react-router';
import Logo from '../../components/Logo/Logo';
import { Box, Button, CircularProgress, Paper, TextField, styled } from '@mui/material';
import { useSnackbar } from 'notistack';
import { APIsContext } from '../../Store/APIs/context';
import { getFullApiUrl } from '../../config/axios';
import { NavigationContext } from '../../Store/Navigation/context';
import { setOptionsActive } from '../../Store/Navigation/reducer';
import Authentication from '../../Store/Authentication/AuthenticationStore';
import { setUpAdjudicatorApis, setUpAdminApis } from '../../Store/APIs/reducer';
import { AuthenticationRole } from '../../Store/Authentication/types';

const LoginContainer = styled(Box)(({ theme }) => ({
    top: "50%",
    left: "50%",
    position: "absolute",
    transform: "translate(-50%, -50%)",
    margin: "auto",
    [theme.breakpoints.up("sm")]: {
        width: "550px"
    },
    [theme.breakpoints.down("sm")]: {
        width: "350px"
    }
}));

const LoginInnerContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "1.5rem",
    [theme.breakpoints.down("sm")]: {
        padding: "1.5rem"
    }
}));

const LoginPage: React.FC<RouteComponentProps> = (props) => {

    const { enqueueSnackbar } = useSnackbar();
    const {state, dispatch} = useContext(APIsContext)
    const [loading, setLoading] = useState(false);
    const navigationDispatch = useContext(NavigationContext).dispatch
    const location = useLocation();

    // This useEffect should fire only when "oauth" is enabled 
    useEffect(() => {
        const login = async () => {
            const params = new URLSearchParams(location.search);
            const code = params.get("code");
            if (code !== null) {
                const code = params.get("code");
                const verifier = sessionStorage.getItem("pkce_code_verifier");
                if (code === null || verifier === null) {
                    return
                }
                state.ManagementApi.oauth2Token({
                    grantType: "authorization_code",
                    clientId: "megamatcherid-pkce",
                    code: code,
                    redirectUri: "http://localhost:40004/login", // TODO: Make this dynamic
                    codeVerifier: verifier,
                }).then(res => {
                    if (res.data && res.data.access_token) {
                        Authentication.loadAuthentication()
                        if (Authentication.isAdminAuthenticated()) {
                            dispatch(setUpAdminApis({
                                basePath: getFullApiUrl(),
                                accessToken: res.data.access_token,
                            }))
                        } else {
                            dispatch(setUpAdjudicatorApis({
                                basePath: getFullApiUrl(),
                                accessToken: res.data.access_token,
                            }))
                        }
                        navigationDispatch(setOptionsActive(true))
                        props.history.push("/")
                    }
                }).catch(e => {
                    enqueueSnackbar("Failed to get token: " + e.message);
                })
            }
        }
        login()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, enqueueSnackbar, dispatch, navigationDispatch, props.history]);

    const handleError = (error: Error) => {
        enqueueSnackbar(error.message, { variant: 'error' });
    };

    const base64urlEncode = (buffer: ArrayBuffer): string => {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }      
        const base64 = btoa(binary);
        return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      }

    const generateCodeVerifier = () => {
        const array = new Uint8Array(32);
        crypto.getRandomValues(array);
        return base64urlEncode(array);
    }

    const generateCodeChallenge = async (verifier: string) => {
        const encoder = new TextEncoder();
        const data = encoder.encode(verifier);
        const digest = await crypto.subtle.digest("SHA-256", data);
        return base64urlEncode(digest);
    }

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setLoading(true);
        const data = new FormData(event.currentTarget);
        state.ManagementApi.login({username: data.get("username") as string, password: data.get("password") as string}).then(async res => {
            if (res && res.data && res.data.authorities && (res.data.authorities[0] !== AuthenticationRole.ROLE_ADMIN && res.data.authorities[0] !== AuthenticationRole.ROLE_ADJUDICATOR)) {
                enqueueSnackbar("Only administrators and adjudicators are allowed", {variant: "error"})
                setLoading(false);
                return
            }
            Authentication.clearAuthentication()
            if (res && res.data && res.data.authorities && res.data.authorities.length > 0) {
                Authentication.authentication = {
                    username: data.get("username") as string,
                    authority: res.data.authorities[0]
                }
                Authentication.saveAuthentication()
            }

            if (res && res.data && res.data.auth_type === "oauth") {

                const codeVerifier = generateCodeVerifier();
                const codeChallenge = await generateCodeChallenge(codeVerifier);
          
                sessionStorage.setItem("pkce_code_verifier", codeVerifier);

                const clientId = "megamatcherid-pkce";
                const redirectUri = "http://localhost:40004/login"; // TODO: Make this dynamic
                const scope = "ADMIN";
    
                const authUrl = new URL(getFullApiUrl() + "/oauth2/authorize");
                authUrl.searchParams.set("response_type", "code");
                authUrl.searchParams.set("client_id", clientId);
                authUrl.searchParams.set("redirect_uri", redirectUri);
                authUrl.searchParams.set("scope", scope);
                authUrl.searchParams.set("code_challenge", codeChallenge);
                authUrl.searchParams.set("code_challenge_method", "S256");
    
                window.location.href = authUrl.toString();
            } else {
                Authentication.loadAuthentication()
                if (Authentication.isAdminAuthenticated()) {
                    dispatch(setUpAdminApis({
                        basePath: getFullApiUrl(),
                        username: data.get("username") as string,
                        password: data.get("password") as string
                    }))
                } else {
                    dispatch(setUpAdjudicatorApis({
                        basePath: getFullApiUrl(),
                        username: data.get("username") as string,
                        password: data.get("password") as string
                    }))
                }
                navigationDispatch(setOptionsActive(true))
                props.history.push("/")
            }
        }).catch(err => {
            if (err.response?.status === 401) {
                enqueueSnackbar("Bad username or password", { variant: 'error' })
                setLoading(false);
                return
            }
            console.log(err.response)
            setLoading(false);
            handleError(err);
        });
    }

    return (
        <LoginContainer>
            <Paper elevation={10}>
                <LoginInnerContainer>
                    <Logo login/>
                    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ marginTop: 1, textAlign: "center" }}>
                        <TextField
                            sx={{margin: "5px"}}
                            required
                            fullWidth
                            id="username"
                            label="Username"
                            name="username"
                            autoFocus
                            inputProps={{
                                autoComplete: "username",
                                style: {paddingTop: "16px", paddingBottom: "16px"}
                            }}
                        />
                        <TextField
                            sx={{margin: "5px"}}
                            required
                            fullWidth
                            id="password"
                            label="Password"
                            name="password"
                            type="password"
                            inputProps={{
                                autoComplete: "current-password",
                                style: {paddingTop: "16px", paddingBottom: "16px"}
                            }}
                        />
                        {loading ?
                            (<CircularProgress sx={{ marginTop: 3, marginBottom: 3 }} />)
                            :
                            (<Button type="submit" size="large" fullWidth variant="contained" sx={{ marginTop: 3, marginBottom: 3 }}>
                                Log In
                            </Button>)}
                    </Box>
                </LoginInnerContainer>
            </Paper>
        </LoginContainer >
    )
}

export default LoginPage
